import React, { useState, useRef, useEffect } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  Modal,
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ImageCropperModal = ({
  open,
  onClose,
  aspectRatio = 1 / 1,
  maxSizeKB = 1024,
  uploadUrl = "/api/upload",
  headers = {},
  onSuccess,
  onError,
}) => {
  const cropperRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (e) => {
    setError(null);
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    if (file.size > maxSizeKB * 1024) {
      setError(`File size exceeds ${maxSizeKB}KB limit`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const getCroppedImageBlob = () => {
    return new Promise((resolve) => {
      if (cropperRef.current) {
        const cropper = cropperRef.current.cropper;
        cropper.getCroppedCanvas().toBlob((blob) => {
          resolve(blob);
        }, "image/jpeg");
      }
    });
  };

  const handleCrop = async () => {
    const blob = await getCroppedImageBlob();
    setCroppedImage(URL.createObjectURL(blob));
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      const blob = await getCroppedImageBlob();
      const formData = new FormData();
      formData.append("image", blob, "cropped-image.png");
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      onSuccess?.(data);
      onClose();
    } catch (err) {
      console.log(err);
      setError(err.message);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setError(null);
    setLoading(false);
  };

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Crop and Upload Image</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!imageSrc ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="image-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="image-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Select Image
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Max size: {maxSizeKB}KB
              </Typography>
            </label>
          </Box>
        ) : (
          <>
            <Cropper
              ref={cropperRef}
              src={imageSrc}
              aspectRatio={aspectRatio}
              viewMode={1}
              guides={true}
              style={{ height: 400, width: "100%" }}
            />

            {croppedImage && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="subtitle2">Preview:</Typography>
                <img
                  src={croppedImage}
                  alt="Cropped preview"
                  style={{ maxWidth: "200px", marginTop: "10px" }}
                />
              </Box>
            )}

            <Box
              sx={{
                mt: 3,
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleCrop}
                disabled={!imageSrc}
              >
                Preview Crop
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleUpload}
                disabled={!croppedImage || loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Upload Image"
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ImageCropperModal;
